import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import type CentralMappingOrganizationSettings from 'lumen/models/central-mapping/organization-settings';
import type MarketSettingsCentralMappingMenuRoute from 'lumen/routes/market/settings/central-mapping/menu';
import {
  CENTRAL_MAPPING_HELP_CENTER_LINKS,
  PERMISSION_VALUES,
  type PermissionValue,
} from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class MarketSettingsCentralMappingMenuController extends Controller {
  declare model: ModelFrom<MarketSettingsCentralMappingMenuRoute>;

  @service declare access: Services['access'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare centralMapping: Services['central-mapping'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked isShowingPermissionsModal = false;
  @tracked permissionAtOpen: PermissionValue = PERMISSION_VALUES.RESTRICT;
  @tracked selectedPermission: PermissionValue = PERMISSION_VALUES.RESTRICT;

  menuHelpCenterLink = CENTRAL_MAPPING_HELP_CENTER_LINKS.MENU_PAGE;

  saveMappingPermissionTask = task({ drop: true }, async (): Promise<void> => {
    const permission = this.mappingPermission;
    if (!permission) {
      return;
    }

    const isHotelEditingAllowed =
      this.selectedPermission === PERMISSION_VALUES.ALLOW;

    permission.allowHotelAdminAndEditorsToEditMapping = isHotelEditingAllowed;

    try {
      await permission.save({
        adapterOptions: { usePatch: true },
      });

      this.toast.success(this.intl.t('general.changes_saved.body'), {
        title: this.intl.t('general.changes_saved'),
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });

      this.isShowingPermissionsModal = false;
      // Re-fetch replaces the orphaned 'default' record with the canonical id after first PATCH.
      void this.model.mappingPermission.perform();
    } catch (error) {
      this.toast.error(this.intl.t('general.error'), {
        showIcon: true,
      });
      permission.rollbackAttributes();
      this.isShowingPermissionsModal = false;
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get canEditPermissions(): boolean {
    return (
      !!this.mappingPermission && this.access.hasCentralMappingMarketEditAccess
    );
  }

  get hierarchyRoute(): string {
    return 'market.settings.central-mapping.hierarchy';
  }

  get isHotelEditingAllowed(): boolean {
    return (
      this.mappingPermission?.allowHotelAdminAndEditorsToEditMapping ?? false
    );
  }

  get isReadOnly(): boolean {
    return !this.access.hasCentralMappingMarketEditAccess;
  }

  get mappingPermission(): CentralMappingOrganizationSettings | null {
    return this.model.mappingPermission.last?.value ?? null;
  }

  get mappingRoute(): string {
    return 'market.settings.central-mapping.mapping';
  }

  @action
  closePermissionsModal(): void {
    this.isShowingPermissionsModal = false;
  }

  @action
  onChangePermission(value: PermissionValue): void {
    this.selectedPermission = value;
  }

  @action
  onSavePermission(): void {
    void this.saveMappingPermissionTask.perform();
  }

  @action
  openPermissionsModal(): void {
    const current = this.isHotelEditingAllowed
      ? PERMISSION_VALUES.ALLOW
      : PERMISSION_VALUES.RESTRICT;
    this.permissionAtOpen = current;
    this.selectedPermission = current;
    this.isShowingPermissionsModal = true;
  }
}
